/**
 * jupiterSwapService — Client-side Jupiter v6 swap execution.
 *
 * Flow:
 *   1. Get quote  → GET /api/trading/jupiter/quote (proxied, CORS-safe)
 *   2. Build tx   → POST /api/trading/jupiter/swap-tx (proxied, gets base64 VersionedTransaction)
 *   3. Deserialize → @solana/web3.js VersionedTransaction.deserialize
 *   4. Sign        → window.solana.signTransaction (Phantom)
 *   5. Send        → Solana RPC sendRawTransaction
 *   6. Confirm     → confirmTransaction
 */

import {
  Connection,
  VersionedTransaction,
} from '@solana/web3.js';
import type { JupiterQuote, LiveOrderResult } from '@arc/shared';

// ─── Solana connection (public RPC) ───────────────────────────────────────────

const SOLANA_RPC = 'https://api.mainnet-beta.solana.com';

function getConnection(): Connection {
  return new Connection(SOLANA_RPC, 'confirmed');
}

// ─── Token mints ──────────────────────────────────────────────────────────────

// Common Solana token mints
export const MINT = {
  SOL:  'So11111111111111111111111111111111111111112',   // Wrapped SOL
  USDC: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
  USDT: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB',
  BTC:  '9n4nbM75f5Ui33ZbPYXn59EwSgE8CGsHtAeTH5YFeJ9E', // Wrapped BTC (legacy)
  ETH:  '7vfCXTUXx5WJV5JADk17DUJ4ksgau7utNKj4b963voxs',  // Wrapped ETH (Wormhole)
  WIF:  'EKpQGSJtjMFqKZ9KQanSqYXRcF8fBopzLHYxdM65zcjm',
  JTO:  'jtojtomepa8beP8AuQc6eXt5FriJwfFMwQx2v2f9mCL',
} as const;

/** Map ARC token symbol → Solana mint address */
export function symbolToMint(symbol: string): string | undefined {
  return (MINT as Record<string, string>)[symbol.toUpperCase()];
}

// ─── Get quote ─────────────────────────────────────────────────────────────────

export interface QuoteParams {
  inputMint:  string;
  outputMint: string;
  amount:     number;           // in input token's smallest unit (lamports / atoms)
  slippageBps?: number;
}

export async function getJupiterQuote(params: QuoteParams): Promise<JupiterQuote> {
  const url = new URL('/api/trading/jupiter/quote', window.location.origin);
  url.searchParams.set('inputMint',  params.inputMint);
  url.searchParams.set('outputMint', params.outputMint);
  url.searchParams.set('amount',     String(params.amount));
  url.searchParams.set('slippage',   String(params.slippageBps ?? 50));

  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`Quote failed: HTTP ${res.status}`);

  const json = await res.json() as { ok: boolean; data?: JupiterQuote; error?: string };
  if (!json.ok || !json.data) throw new Error(json.error ?? 'No quote data');
  return json.data;
}

// ─── Execute swap ──────────────────────────────────────────────────────────────

export async function executeJupiterSwap(
  quote:         JupiterQuote,
  publicKey:     string,
): Promise<LiveOrderResult> {
  try {
    // Step 1: Build swap transaction (proxied to Jupiter)
    const swapRes = await fetch('/api/trading/jupiter/swap-tx', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        quoteResponse: quote,
        userPublicKey: publicKey,
      }),
    });

    if (!swapRes.ok) {
      return { success: false, error: `Swap TX build failed: HTTP ${swapRes.status}` };
    }

    const swapJson = await swapRes.json() as {
      ok: boolean;
      data?: { swapTransaction: string; lastValidBlockHeight: number };
      error?: string;
    };

    if (!swapJson.ok || !swapJson.data) {
      return { success: false, error: swapJson.error ?? 'No swap transaction returned' };
    }

    const { swapTransaction, lastValidBlockHeight } = swapJson.data;

    // Step 2: Deserialize the versioned transaction
    const txBytes = Buffer.from(swapTransaction, 'base64');
    const tx      = VersionedTransaction.deserialize(txBytes);

    // Step 3: Sign with Phantom
    const phantom = window.solana;
    if (!phantom?.isPhantom) {
      return { success: false, error: 'Phantom wallet not available' };
    }

    const signedTx = await phantom.signTransaction(tx);

    // Step 4: Send to Solana
    const connection  = getConnection();
    const rawTx       = signedTx.serialize();
    const signature   = await connection.sendRawTransaction(rawTx, {
      skipPreflight:        false,
      preflightCommitment:  'confirmed',
      maxRetries:           3,
    });

    // Step 5: Confirm
    const latestBlock = await connection.getLatestBlockhash();
    await connection.confirmTransaction(
      {
        signature,
        blockhash:            latestBlock.blockhash,
        lastValidBlockHeight,
      },
      'confirmed',
    );

    return { success: true, txHash: signature };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error('[Jupiter] swap error:', msg);
    return { success: false, error: msg };
  }
}

// ─── USDC balance on Solana ────────────────────────────────────────────────────

const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';

export async function getUsdcBalance(walletAddress: string): Promise<number> {
  try {
    const connection = getConnection();
    const { value } = await connection.getParsedTokenAccountsByOwner(
      // PublicKey from string
      { toBytes: () => Buffer.from(walletAddress, 'base64') } as never,
      { mint: { toBase58: () => USDC_MINT } as never },
    );

    // Sum all USDC token accounts
    const total = value.reduce((sum, acc) => {
      const amount = acc.account.data.parsed?.info?.tokenAmount?.uiAmount as number | undefined;
      return sum + (amount ?? 0);
    }, 0);

    return total;
  } catch {
    return 0;
  }
}
